import React, { ReactNode } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { WorkGeoLocationDTO, WorkGeoLocationPointDTO, WorkGeoLocationPolygonDTO } from '../../../../api/core';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { Panel } from '../layout/Panel';
import {
  getDefaultWorkGeoLocation,
  getDefaultWorkGeoLocationPoint,
  getDefaultWorkGeoLocationPolygon,
} from '../../../../Settings/emptyModels';
import { JustifiedDeleteButton } from '../../../components/form/MultiInput/Buttons';
import { Fieldset } from '../layout/Fieldset';
import { InputGroup } from '../layout/InputGroup';
import { LabelledInputText } from '../../../components/form/LabelledInputText';

interface Props {
  workGeoLocations: WorkGeoLocationDTO[];
  onChange: (workGeoLocations: WorkGeoLocationDTO[]) => void;
  checkPermission: CheckPermissionFunction;
  validation: ValidationResult;
  pubtypeGroup: string;
  autoFocus: boolean;
}

export function GeoLocations(props: Props): ReactNode {
  const intl: IntlShape = useIntl();

  return (
    <Panel key={'geolocations'} className={'col-12'} title={'work.attr.geoLocations'}>
      <MultiInputControls<WorkGeoLocationDTO>
        canEdit={props.checkPermission().canWrite}
        lines={props.workGeoLocations}
        addNew={(rows: WorkGeoLocationDTO[]): number => rows.push(getDefaultWorkGeoLocation())}
        onChange={props.onChange}
        renderLines={(workGeoLocation: WorkGeoLocationDTO, onLineChange: (workGeoLocationDTO: WorkGeoLocationDTO) => void, actions: Actions, idx: number): ReactNode => {
          return (
            <Fieldset toggleable={true} pt={{root: {className: 'surface-100'}}}>
              <InputGroup>
                <LabelledInputText
                  autoFocus={props.autoFocus}
                  id={`geoLoc-${idx}-pointLatitude`}
                  value={workGeoLocation.pointLatitude || ''}
                  permissions={props.checkPermission('point_latitude')}
                  validation={props.validation.children[idx].fields.point_latitude}
                  onChange={(pointLatitude: string): void => onLineChange({...workGeoLocation, pointLatitude})}
                  label={`work.attr.geoLocation.attr.latitude`}
                />
                <LabelledInputText
                  id={`geoLoc-${idx}-pointLongitude`}
                  value={workGeoLocation.pointLongitude || ''}
                  permissions={props.checkPermission('point_longitude')}
                  validation={props.validation.children[idx].fields.point_longitude}
                  onChange={(pointLongitude: string): void => onLineChange({...workGeoLocation, pointLongitude})}
                  label={`work.attr.geoLocation.attr.longitude`}
                />
              </InputGroup>
              <h4>{intl.formatMessage({id: `work.attr.geoLocation.attr.geoLocationBox.label`})}</h4>
              <InputGroup>
                <LabelledInputText
                  id={`geoLoc-${idx}-boxNorthBoundLatitude`}
                  value={workGeoLocation.boxNorthBoundLatitude || ''}
                  permissions={props.checkPermission('box_north_bound_latitude')}
                  validation={props.validation.children[idx].fields.box_north_bound_latitude}
                  onChange={(boxNorthBoundLatitude: string): void => onLineChange({
                    ...workGeoLocation,
                    boxNorthBoundLatitude,
                  })}
                  label={`work.attr.geoLocation.attr.boxNorthBoundLatitude`}
                />
                <LabelledInputText
                  id={`geoLoc-${idx}-boxSouthBoundLatitude`}
                  value={workGeoLocation.boxSouthBoundLatitude || ''}
                  permissions={props.checkPermission('box_south_bound_latitude')}
                  validation={props.validation.children[idx].fields.box_south_bound_latitude}
                  onChange={(boxSouthBoundLatitude: string): void => onLineChange({
                    ...workGeoLocation,
                    boxSouthBoundLatitude,
                  })}
                  label={`work.attr.geoLocation.attr.boxSouthBoundLatitude`}
                />
                <LabelledInputText
                  id={`geoLoc-${idx}-boxEastBoundLongitude`}
                  value={workGeoLocation.boxEastBoundLongitude || ''}
                  permissions={props.checkPermission('box_east_bound_longitude')}
                  validation={props.validation.children[idx].fields.box_east_bound_longitude}
                  onChange={(boxEastBoundLongitude: string): void => onLineChange({
                    ...workGeoLocation,
                    boxEastBoundLongitude,
                  })}
                  label={`work.attr.geoLocation.attr.boxEastBoundLongitude`}
                />
                <LabelledInputText
                  id={`geoLoc-${idx}-boxWestBoundLongitude`}
                  value={workGeoLocation.boxWestBoundLongitude || ''}
                  permissions={props.checkPermission('box_west_bound_longitude')}
                  validation={props.validation.children[idx].fields.box_west_bound_longitude}
                  onChange={(boxWestBoundLongitude: string): void => onLineChange({
                    ...workGeoLocation,
                    boxWestBoundLongitude,
                  })}
                  label={`work.attr.geoLocation.attr.boxWestBoundLongitude`}
                />
              </InputGroup>
              <LabelledInputText
                id={`geoLoc-${idx}-place`}
                value={workGeoLocation.place || ''}
                permissions={props.checkPermission('place')}
                validation={props.validation.children[idx].fields.place}
                onChange={(place: string): void => onLineChange({...workGeoLocation, place})}
                label={`work.attr.geoLocation.attr.place`}
              />
              <h4>{intl.formatMessage({id: `work.attr.geoLocation.attr.geoLocationPolygon.label`})}</h4>
              <MultiInputControls
                canEdit={true}
                lines={workGeoLocation.workGeoLocationPolygon ? [workGeoLocation.workGeoLocationPolygon] : []}
                onChange={(rows: WorkGeoLocationPolygonDTO[]): void => onLineChange({
                  ...workGeoLocation,
                  workGeoLocationPolygon: rows.pop() || null,
                })}
                addNew={(rows: WorkGeoLocationPolygonDTO[]): number => rows.push(getDefaultWorkGeoLocationPolygon())}
                max={1}
                renderLines={(polygon: WorkGeoLocationPolygonDTO, onPolygonChange: (workGeoLocationPolygonDTO: WorkGeoLocationPolygonDTO) => void, polygonActions: Actions, polygonIdx: number): ReactNode => {
                  return <Fieldset>
                    <h4>{intl.formatMessage({id: `work.attr.geoLocation.attr.geoLocationPolygon.attr.geoLocationInPolygonPoints.label`})}</h4>
                    <InputGroup>
                      <LabelledInputText
                        id={`geoLoc-${polygonIdx}-polygon-in-latitude`}
                        autoFocus={props.autoFocus}
                        value={polygon.inLatitude}
                        permissions={props.checkPermission('work_geo_location_polygon.in_latitude')}
                        validation={props.validation.children[polygonIdx].fields.work_geo_location_polygon.child.fields.in_latitude}
                        onChange={(inLatitude: string): void => onPolygonChange({...polygon, inLatitude})}
                        label={`work.attr.geoLocation.attr.latitude`}
                      />
                      <LabelledInputText
                        id={`geoLoc-${polygonIdx}-polygon-in-longitude`}
                        value={polygon.inLongitude}
                        permissions={props.checkPermission('work_geo_location_polygon.in_longitude')}
                        validation={props.validation.children[polygonIdx].fields.work_geo_location_polygon.child.fields.in_longitude}
                        onChange={(inLongitude: string): void => onPolygonChange({...polygon, inLongitude})}
                        label={`work.attr.geoLocation.attr.longitude`}
                      />
                    </InputGroup>
                    <h4>{intl.formatMessage({id: `work.attr.geoLocation.attr.geoLocationPolygon.attr.geoLocationPolygonPoint.label`})}</h4>
                    <MultiInputControls<WorkGeoLocationPointDTO>
                      canEdit={true}
                      lines={polygon.workGeoLocationPoints}
                      onChange={(workGeoLocationPoints: WorkGeoLocationPointDTO[]): void => onPolygonChange({
                        ...polygon,
                        workGeoLocationPoints,
                      })}
                      min={4}
                      addNew={(rows: WorkGeoLocationPointDTO[]): number => rows.push(getDefaultWorkGeoLocationPoint())}
                      renderLines={(point: WorkGeoLocationPointDTO, onPointChange: (workGeoLocationPointDTO: WorkGeoLocationPointDTO) => void, pointActions: Actions, pointIdx: number): ReactNode => {
                        return <InputGroup
                          key={pointIdx}
                          delete={{
                            action: pointActions.delete,
                            disabled: !props.checkPermission('work_geo_location_polygon.work_geo_location_points').canWrite,
                          }}
                        >
                          <LabelledInputText
                            id={`geoLoc-${polygonIdx}-polygon-point-${pointIdx}-latitude`}
                            autoFocus={props.autoFocus}
                            value={point.latitude}
                            permissions={props.checkPermission('work_geo_location_polygon.work_geo_location_points.latitude')}
                            validation={props.validation.children[polygonIdx].fields.work_geo_location_polygon.child.fields.work_geo_location_points.children[polygonIdx].fields.latitude}
                            onChange={(latitude: string): void => onPointChange({...point, latitude})}
                            label={`work.attr.geoLocation.attr.latitude`}
                          />
                          <LabelledInputText
                            id={`geoLoc-${polygonIdx}-polygon-point-${pointIdx}-longitude`}
                            value={point.longitude}
                            permissions={props.checkPermission('work_geo_location_polygon.work_geo_location_points.longitude')}
                            validation={props.validation.children[polygonIdx].fields.work_geo_location_polygon.child.fields.work_geo_location_points.children[polygonIdx].fields.longitude}
                            onChange={(longitude: string): void => onPointChange({...point, longitude})}
                            label={`work.attr.geoLocation.attr.longitude`}
                          />
                        </InputGroup>;
                      }}/>
                    <JustifiedDeleteButton onClick={polygonActions.delete}
                                           disabled={!props.checkPermission('work_geo_location_polygon').canWrite}/>
                  </Fieldset>;
                }}
              />
              <JustifiedDeleteButton onClick={actions.delete} disabled={!props.checkPermission().canWrite}/>
            </Fieldset>
          );
        }}
      />
    </Panel>
  );
}

